import {
  AlertTriangle,
  Ban,
  CheckCircle2,
  Clock,
  Eye,
  Gift,
  type LucideIcon,
  MapPin,
  Package,
  PackageCheck,
  ShoppingBag,
  Sparkles,
  Truck,
  XCircle,
} from "lucide-react"
import { useTranslation } from "react-i18next"

import { cn } from "@/lib/utils"
import type { Collab, CollabState } from "@/types/collab"

/**
 * OKS-480 — MissionCard v2 status driver.
 *
 * The full 14-state lifecycle alphabet the status-driven card can render: the
 * 12 `CollabState`s plus two capacity-only states with no data path yet —
 * `flagged` (a submission-flag UI gap; the collab stays `posted` today,
 * lifecycle §1) and `expired` (net-new expiry backend, lifecycle §5). Both
 * render via prop/story only this ticket.
 */
export type MissionCardStatus =
  | CollabState
  | "flagged"
  | "expired"
  // OKS-1001 — a RENDER VARIANT of `to_ship`, not a lifecycle state. The collab
  // is in `to_ship` on the wire and in the state machine; what differs is who
  // the card should say is holding the parcel up. It joins `flagged` and
  // `expired`, which have never been `CollabState`s either — this union has
  // always been the alphabet the CARD can render, not the one the server sends.
  | "to_ship_address_pending"
  // OKS-1040 — `to_ship`, waiting on the creator's PRODUCT choice. Derived
  // BEFORE the address variant (UX §2.2): the page puts the choice first.
  | "to_ship_product_pending"

/**
 * OKS-1001 — the single derivation from a collab to the chip it renders.
 *
 * ⚠️ EXPORTED ON PURPOSE, and it exists to be the thing the next surface
 * reaches for. `MissionDrop` and `WelcomeBack` mount `MissionCard` today
 * WITHOUT a status, so this has exactly one call site — which is precisely the
 * condition under which a future page writes `status={collab.state}`, silently
 * loses the variant, and reopens OKS-1001 on a surface nobody thought to check.
 * An inline ternary at the call site is invisible to that author; this sits
 * next to the type they are already importing.
 *
 * ⚠️ ONE input. The chip must not take the state and the flag as separate
 * props: two inputs that can disagree about one collab is the shape of the bug
 * this ticket closes (the CTA bar said the brand was packing while the address
 * block on the same page asked for an address).
 *
 * Teaser/`locked` handling stays at the call site — that is a disclosure
 * concern, not a status derivation.
 */
export function missionCardStatusFor(collab: Collab): MissionCardStatus {
  // OKS-1040 — the product choice precedes the address (UX §2.2). The server
  // emits `product_choice_required` only when true (absent otherwise).
  if (collab.product_choice_required) return "to_ship_product_pending"
  return collab.address_action_required
    ? "to_ship_address_pending"
    : collab.state
}

/**
 * Overlay chip tone. Reuses the `state-badge.tsx` tone vocabulary (UX Spec §3)
 * but with solid, cover-legible fills (the chip overlays a photo), mirroring
 * the `UrgencyChip` overlay treatment. Every tone is paired with a distinct
 * ICON + TEXT so the signal is never colour-only (a11y, UX Spec §5).
 */
type StatusTone =
  | "gray"
  | "muted"
  | "blue"
  | "sky"
  | "amber"
  | "indigo"
  | "teal"
  | "orange"
  | "green"
  | "destructive"

interface StatusConfig {
  icon: LucideIcon
  tone: StatusTone
  /** i18n key under `missions.card.status.*`. */
  i18nKey: string
}

// i18n keys resolved via `t(config.i18nKey)` — declared so the static-AST
// scanner sees each as live:
// inertia-i18n-use missions.card.status.assigned missions.card.status.applied missions.card.status.accepted missions.card.status.rejected missions.card.status.purchased missions.card.status.to_ship missions.card.status.shipped missions.card.status.delivered missions.card.status.posted missions.card.status.declined missions.card.status.issue missions.card.status.completed missions.card.status.flagged missions.card.status.expired missions.card.status.reopen_requested missions.card.status.to_ship_address_pending missions.card.status.to_ship_product_pending
const STATUS_CONFIG: Record<MissionCardStatus, StatusConfig> = {
  assigned: {
    icon: Sparkles,
    tone: "gray",
    i18nKey: "missions.card.status.assigned",
  },
  applied: {
    icon: Clock,
    tone: "sky",
    i18nKey: "missions.card.status.applied",
  },
  accepted: {
    icon: CheckCircle2,
    tone: "blue",
    i18nKey: "missions.card.status.accepted",
  },
  purchased: {
    icon: ShoppingBag,
    tone: "amber",
    i18nKey: "missions.card.status.purchased",
  },
  to_ship: {
    icon: Package,
    tone: "amber",
    i18nKey: "missions.card.status.to_ship",
  },
  // OKS-1001 — `to_ship`, but the creator's address is what the parcel is
  // waiting on. `MapPin` matches the `confirm_address` CTA they tap next and
  // breaks hard from `to_ship`'s `Package`.
  //
  // ⚠️ `orange` is shared with `posted`, and that is the existing pattern
  // rather than a compromise: EVERY tone in this vocabulary carries two
  // statuses (`amber` is `purchased` + `to_ship`, `destructive` is `flagged` +
  // `issue`). Tone is a coarse band here; the icon and the label identify the
  // chip, which is also why colour is never the sole signal. Not `amber` — it
  // would read as the chip it replaces in a scanned list. Not `destructive` —
  // red is this app's fault tone, and an address awaiting confirmation is a
  // normal step, not a failure.
  to_ship_address_pending: {
    icon: MapPin,
    tone: "orange",
    i18nKey: "missions.card.status.to_ship_address_pending",
  },
  // OKS-1040 — `Gift` matches the Choose block the creator taps next; same
  // `orange` band as the address variant (a normal creator-owned step, not a
  // fault). The label and icon carry the meaning, never the tone alone.
  to_ship_product_pending: {
    icon: Gift,
    tone: "orange",
    i18nKey: "missions.card.status.to_ship_product_pending",
  },
  shipped: {
    icon: Truck,
    tone: "indigo",
    i18nKey: "missions.card.status.shipped",
  },
  delivered: {
    icon: PackageCheck,
    tone: "teal",
    i18nKey: "missions.card.status.delivered",
  },
  posted: { icon: Eye, tone: "orange", i18nKey: "missions.card.status.posted" },
  flagged: {
    icon: AlertTriangle,
    tone: "destructive",
    i18nKey: "missions.card.status.flagged",
  },
  issue: {
    icon: AlertTriangle,
    tone: "destructive",
    i18nKey: "missions.card.status.issue",
  },
  completed: {
    icon: CheckCircle2,
    tone: "green",
    i18nKey: "missions.card.status.completed",
  },
  expired: {
    icon: Ban,
    tone: "muted",
    i18nKey: "missions.card.status.expired",
  },
  // OKS-483 — reopen-requested interim (creator asked the brand to reopen).
  reopen_requested: {
    icon: Clock,
    tone: "blue",
    i18nKey: "missions.card.status.reopen_requested",
  },
  // Server-filtered from the creator wire (lifecycle §1); typed for
  // exhaustiveness so `status={collab.state}` never falls through.
  rejected: {
    icon: XCircle,
    tone: "muted",
    i18nKey: "missions.card.status.rejected",
  },
  declined: {
    icon: XCircle,
    tone: "muted",
    i18nKey: "missions.card.status.declined",
  },
}

const TONE_CLASSES: Record<StatusTone, string> = {
  gray: "bg-foreground/80 text-background",
  muted: "bg-muted-foreground/80 text-background",
  blue: "bg-blue-600 text-white",
  sky: "bg-sky-600 text-white",
  amber: "bg-amber-500 text-white",
  indigo: "bg-indigo-600 text-white",
  teal: "bg-teal-600 text-white",
  orange: "bg-orange-500 text-white",
  green: "bg-emerald-600 text-white",
  destructive: "bg-destructive text-white",
}

interface StatusChipProps {
  status: MissionCardStatus
  className?: string
}

/**
 * OKS-480 — Status chip for the MissionCard cover (top-left overlay). The
 * single `status` driver presets text + tone + icon ("one prop = one status").
 * Replaces the urgency chip in the top-left for calm rows; the `overdue`
 * overlay keeps the `UrgencyChip` (UX Spec §1/§6). Icon + colour + text — never
 * colour alone.
 */
export function StatusChip({ status, className }: StatusChipProps) {
  const { t } = useTranslation()
  const { icon: Icon, tone, i18nKey } = STATUS_CONFIG[status]

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold shadow-sm",
        TONE_CLASSES[tone],
        className,
      )}
      data-testid="mission-card-status-chip"
      data-status={status}
      data-tone={tone}
    >
      <Icon className="size-3" aria-hidden="true" />
      {t(i18nKey)}
    </span>
  )
}
